import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import ScrollableWrapper from "../common/ScrollableWrapper";
import { useSelector } from "react-redux";
import { RootStatePlatform } from "../../store/platform-slice";
import { useEffect, useRef } from "react";
import SystemScoreHeader from "./SystemScoreHeader";
import { useScoreSidePanel } from "./ScoreSidePanelContext";
import { useAuth } from "../../../../providers/AuthProvider";
import {
  getCategoryColor,
  getBiomarkerCategoryColor,
  enPrecisionTestKind,
  SCORE_CATEGORY_LABELS,
  isHighLowMetric,
  getHighLowLabel,
  formatBiomarkerTabValue,
  isRangeBiomarkerValue,
} from "../../store/helper";
import { BIOMARKER_CATEGORY_LABELS, NO_STATUS_LABEL } from "./usePanelData";
import CustomTooltip from "../common/CustomTooltip";
import ActionButton from "../../../../common/ui/ActionButton";
import EditSvg from "../../../../common/svgs/EditSvg";

type Props = {
  // Health overview passes this so "Manage {system} Biomarkers" opens the
  // All-biomarkers view scoped to the current system (redux selectedSystem)
  // instead of leaving for the old Biomarkers tab.
  onManageBiomarkers?: () => void;
};

const SystemDetails = ({ onManageBiomarkers }: Props) => {
  const theme: Theme = useTheme();
  const {
    openPanel,
    isOpen,
    contentType,
    metricData,
    breathBiomarkerData,
    bloodBiomarkerData,
    // Aliased: `userBiomarkerData` locally means the biomarkers resource.
    userBiomarkerData: openUserBiomarkerData,
    highlightedBiomarkerIds,
  } = useScoreSidePanel();
  const scrollableRef = useRef<HTMLDivElement>(null);
  const { AuthUser } = useAuth();
  const isAffiliate = AuthUser?.role !== 1;
  const reportData = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer.score
  );
  const isDemoReport = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer.isDemo
  );

  // User-recorded biomarkers (biomarkers resource). When a blood biomarker in
  // this system also exists there, clicking it opens the Health overview
  // biomarker panel (About/Readings) instead of the report-backed panel.
  const userBiomarkerData = useSelector(
    (state: RootStatePlatform) => state.platform.biomarkerData
  );
  const findUserBiomarker = (id: string): any =>
    (Object.values(userBiomarkerData || {}) as any[])
      .flat()
      .find((b: any) => b.biomarkerID === id);

  const hasUserBiomarker = (id: string): boolean => !!findUserBiomarker(id);

  const dashboardData = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer.dashboard
  );

  const biomarkerList = useSelector(
    (state: RootStatePlatform) => state.platform.biomarkerList
  );

  const selectedSystem = useSelector(
    (state: RootStatePlatform) => state.platform.selectedSystem
  );

  // `reportData` is briefly undefined right after a mutation clears
  // dataPlatformServer (e.g. saving the Review health scores modal), until
  // the report refetch lands.
  const data = (reportData as any)?.systems?.find(
    (group: any) => group.system === selectedSystem
  );

  useEffect(() => {
    if (scrollableRef.current) {
      scrollableRef.current.scrollTop = 0;
    }
    if (isOpen) {
      openPanel("system");
    }
  }, [selectedSystem]);

  useEffect(() => {
    if (highlightedBiomarkerIds.size > 0 && scrollableRef.current) {
      const firstHighlighted = scrollableRef.current.querySelector(
        "[data-highlighted='true']"
      );
      if (firstHighlighted) {
        firstHighlighted.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [highlightedBiomarkerIds]);

  // A system the report carries no entry for has no detail to show — the
  // report omits a system entirely when it scored nothing for it (Body
  // Composition Analysis on a client with no PNOE test, reachable from the
  // All-biomarkers system dropdown). Render nothing rather than an empty
  // column; the body map beside it still shows what that system has.
  if (selectedSystem !== 0 && !data) return null;

  // Build PNOE metrics by matching score.breathBiomarkers with dashboard.metrics
  const pnoeMetrics = (data?.breathBiomarkers || [])
    .map((breathBio: any) => {
      const dashMetric = dashboardData?.metrics?.find(
        (m: any) => m.id === breathBio.id
      );
      if (!dashMetric) return null;

      // Resolve nested biomarkers from dashboard.breathbiomarkers
      const biomarkers = (breathBio.biomarkers || [])
        .map((nested: any) => {
          const dashBio: any = dashboardData?.breathbiomarkers?.find(
            (b: any) => b.id === nested.id
          );
          if (!dashBio) return null;
          return {
            id: dashBio.id,
            label: dashBio.label,
            description: dashBio.description,
            value: dashBio.value,
            unit: dashBio.unit,
            score: dashBio.score,
            scoreCategory: dashBio.scoreCategory,
            values: dashBio.values,
          };
        })
        .filter(Boolean);

      return {
        id: dashMetric.id,
        name: dashMetric.label,
        score: dashMetric.score,
        scoreCategory: dashMetric.scoreCategory,
        value: dashMetric.value,
        unit: dashMetric.unit,
        description: dashMetric.description,
        whyItIsImportantToTrackText: dashMetric.whyItIsImportantToTrackText,
        howItsMeasuredText: dashMetric.howItsMeasuredText,
        values: dashMetric.values,
        influencedBy: dashMetric.influencedBy,
        affectsThis: dashMetric.affectsThis,
        biomarkers,
      };
    })
    .filter(Boolean);

  // Build blood biomarkers by matching score.bloodBiomarkers with dashboard.bloodbiomarkers, grouped by kind
  const bloodBiomarkersGrouped: Record<number, any[]> = {};
  (data?.bloodBiomarkers || []).forEach((bb: any) => {
    const dashBio = dashboardData?.bloodbiomarkers?.find(
      (d: any) => d.id === bb.id
    );
    if (!dashBio) return;
    const kind = dashBio.kind as number;
    if (!bloodBiomarkersGrouped[kind]) bloodBiomarkersGrouped[kind] = [];
    const biomarkerInfo = biomarkerList?.find((b: any) => b.id === dashBio.id);
    // The report payload only carries a numeric `value`, which is a meaningless
    // 0 for qualitative biomarkers (LDL Pattern → "0" instead of "A") and drops
    // the operators of range values. The biomarkers resource holds the current
    // value in full (value / qvalue / range_*), so render that for those two
    // shapes; plain numeric biomarkers keep the report value.
    const userItem = findUserBiomarker(dashBio.id);
    const displayValue =
      userItem && (userItem.qualitative || isRangeBiomarkerValue(userItem))
        ? formatBiomarkerTabValue(userItem, userItem.unit, userItem.qualitative)
        : undefined;
    bloodBiomarkersGrouped[kind].push({
      id: dashBio.id,
      label: dashBio.shortname || dashBio.label,
      fullName: dashBio.label,
      description: biomarkerInfo?.text || dashBio.description,
      value: dashBio.value,
      unit: dashBio.unit,
      displayValue,
      score: dashBio.score,
      scoreCategory: dashBio.scoreCategory,
      values: dashBio.values,
      influencedBy: dashBio.influencedBy,
      affectsThis: dashBio.affectsThis,
    });
  });
  const hasBloodBiomarkers = Object.keys(bloodBiomarkersGrouped).length > 0;

  return (
    <div style={styles.container(theme)}>
      <ScrollableWrapper ref={scrollableRef} customPadding={"0"}>
        <div style={styles.inner(theme)}>
          {data && (
            <>
              {data.approval !== 4 ? (
                <div style={styles.systemTitle(theme)}>{data.name}</div>
              ) : (
                <SystemScoreHeader
                  name={data.name}
                  score={data.score}
                  scoreCategory={data.scoreCategory}
                  onClick={() => openPanel("system")}
                />
              )}

              {/* PNOE metrics section */}
              {pnoeMetrics.length > 0 && (
                <div style={styles.metricsSection}>
                  <div style={styles.sectionHeader}>
                    <div style={styles.sectionTitle(theme)}>PNOE metrics</div>
                    <div style={styles.sectionSubtitle(theme)}>
                      Calculated by breath biomarkers
                    </div>
                  </div>
                  {pnoeMetrics.map((metric: any, idx: number) => (
                    <MetricCard
                      key={idx}
                      metric={metric}
                      theme={theme}
                      isHighlighted={highlightedBiomarkerIds.has(metric.id)}
                      isSelected={
                        isOpen &&
                        contentType === "metric" &&
                        metricData?.id === metric.id
                      }
                      selectedBreathBiomarkerId={
                        isOpen && contentType === "breathBiomarker"
                          ? breathBiomarkerData?.id ?? null
                          : null
                      }
                      onMetricClick={() =>
                        openPanel("metric", {
                          metricData: {
                            id: metric.id,
                            name: metric.name,
                            description: metric.description,
                            value: metric.value,
                            unit: metric.unit,
                            score: metric.score,
                            scoreCategory: metric.scoreCategory,
                            values: metric.values,
                            whyItIsImportantToTrackText:
                              metric.whyItIsImportantToTrackText,
                            howItsMeasuredText: metric.howItsMeasuredText,
                            influencedBy: metric.influencedBy,
                            affectsThis: metric.affectsThis,
                          },
                        })
                      }
                      onBiomarkerClick={(bio: any) =>
                        openPanel("breathBiomarker", {
                          breathBiomarkerData: {
                            id: bio.id,
                            label: bio.label,
                            description: bio.description,
                            value: bio.value,
                            unit: bio.unit,
                            score: bio.score,
                            scoreCategory: bio.scoreCategory,
                            values: bio.values,
                          },
                        })
                      }
                    />
                  ))}
                </div>
              )}

              {/* Blood biomarkers section */}
              {hasBloodBiomarkers && (
                <div style={styles.metricsSection}>
                  <div style={styles.sectionTitle(theme)}>Biomarkers</div>
                  {Object.entries(bloodBiomarkersGrouped).map(
                    ([kind, biomarkers]) => (
                      <div key={kind} style={styles.bloodKindGroup}>
                        <div style={styles.sectionSubtitle(theme)}>
                          {enPrecisionTestKind[Number(kind)] || "Other"}
                        </div>
                        {biomarkers.map((bio: any, idx: number) => (
                          <BloodBiomarkerCard
                            key={idx}
                            biomarker={bio}
                            theme={theme}
                            isHighlighted={highlightedBiomarkerIds.has(bio.id)}
                            isSelected={
                              isOpen &&
                              ((contentType === "bloodBiomarker" &&
                                bloodBiomarkerData?.id === bio.id) ||
                                (contentType === "userBiomarker" &&
                                  openUserBiomarkerData?.biomarkerID ===
                                    bio.id))
                            }
                            onClick={() => {
                              if (hasUserBiomarker(bio.id)) {
                                openPanel("userBiomarker", {
                                  userBiomarkerData: {
                                    biomarkerID: bio.id,
                                    // Opened from the faded sample preview →
                                    // the panel locks its Readings tab.
                                    sampleContext: isDemoReport,
                                  },
                                });
                                return;
                              }
                              openPanel("bloodBiomarker", {
                                bloodBiomarkerData: {
                                  id: bio.id,
                                  label: bio.label,
                                  fullName: bio.fullName,
                                  description: bio.description,
                                  value: bio.value,
                                  unit: bio.unit,
                                  score: bio.score,
                                  scoreCategory: bio.scoreCategory,
                                  values: bio.values,
                                  influencedBy: bio.influencedBy,
                                  affectsThis: bio.affectsThis,
                                },
                              });
                            }}
                          />
                        ))}
                      </div>
                    )
                  )}
                </div>
              )}

              {/* No biomarker data yet for this system */}
              {!hasBloodBiomarkers && (
                <div style={styles.emptyBiomarkers(theme)}>
                  <div style={styles.emptyBiomarkersTitle(theme)}>
                    No biomarker data for {data.name}
                  </div>
                  <div style={styles.emptyBiomarkersSubtitle(theme)}>
                    {isAffiliate ? (
                      <>
                        Use <b>Add results</b> to calculate the score
                      </>
                    ) : (
                      "No biomarker results have been recorded yet"
                    )}
                  </div>
                </div>
              )}

              {/* Affiliate-only jump into the Biomarkers tab for this client.
                  Hidden on sample reports — the detail column is a faded
                  sample preview, and real biomarker entry goes through the
                  toolbar's All biomarkers / Add results. */}
              {isAffiliate && !isDemoReport && (
                <ActionButton
                  label={`Manage ${data.name} Biomarkers`}
                  icon={<EditSvg color={theme.colors_ds.PRIMARY_ICON_MAIN} />}
                  onClick={() => onManageBiomarkers?.()}
                  sx={{ width: "100%", justifyContent: "center" }}
                  centerContent
                />
              )}
            </>
          )}
        </div>
      </ScrollableWrapper>
    </div>
  );
};

// --- MetricCard Component ---
type MetricCardProps = {
  metric: any;
  theme: Theme;
  isHighlighted?: boolean;
  isSelected?: boolean;
  selectedBreathBiomarkerId?: string | null;
  onMetricClick?: () => void;
  onBiomarkerClick?: (bio: any) => void;
};

export const MetricCard = ({
  metric,
  theme,
  isHighlighted,
  isSelected,
  selectedBreathBiomarkerId,
  onMetricClick,
  onBiomarkerClick,
}: MetricCardProps) => {
  const scoreColor = getCategoryColor(
    "%",
    metric.score,
    theme,
    metric.scoreCategory
  );
  const biomarkers = metric.biomarkers || [];

  return (
    <div
      style={styles.metricCard(theme, isSelected, isHighlighted)}
      data-highlighted={isHighlighted || undefined}
      onClick={onMetricClick}
    >
      {/* Header row */}
      <div style={styles.metricHeader}>
        <div style={styles.metricNameRow}>
          <span style={styles.metricName(theme)}>{metric.name}</span>
        </div>
        {isHighLowMetric(metric.id) ? (
          <div style={styles.scoreBadge(theme)}>
            <span style={styles.scoreBadgeText(scoreColor)}>
              {getHighLowLabel(metric.score)}
            </span>
          </div>
        ) : (
          <CustomTooltip
            message={`Status: ${
              SCORE_CATEGORY_LABELS[metric.scoreCategory || 1]
            }`}
            customHeight="auto"
          >
            <div style={styles.scoreBadge(theme)}>
              <span style={styles.scoreBadgeText(scoreColor)}>
                {metric.score}%
              </span>
            </div>
          </CustomTooltip>
        )}
      </div>

      {/* Biomarkers area */}
      {biomarkers.length > 0 && (
        <div style={styles.metricBiomarkersArea(theme, isHighlighted)}>
          {biomarkers.map((bio: any, idx: number) => {
            const bioColor =
              getBiomarkerCategoryColor(
                bio.scoreCategory,
                theme,
                "biomarkers"
              ) ?? "";
            const isBioSelected = selectedBreathBiomarkerId === bio.id;

            return (
              <div
                key={idx}
                style={styles.biomarkerItemCard(theme, isBioSelected)}
                onClick={(e) => {
                  e.stopPropagation();
                  onBiomarkerClick?.(bio);
                }}
              >
                <div style={styles.biomarkerItemRow}>
                  <div style={styles.biomarkerDotAndName}>
                    <div style={styles.biomarkerDot(bioColor)} />
                    <span style={styles.biomarkerName(theme)}>{bio.label}</span>
                  </div>
                  <CustomTooltip
                    message={
                      BIOMARKER_CATEGORY_LABELS[bio.scoreCategory]
                        ? `Status: ${
                            BIOMARKER_CATEGORY_LABELS[bio.scoreCategory]
                          }`
                        : NO_STATUS_LABEL
                    }
                    customHeight="auto"
                  >
                    <span style={styles.biomarkerValue(bioColor)}>
                      {bio.value} {bio.unit}
                    </span>
                  </CustomTooltip>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// --- BloodBiomarkerCard Component ---
export const BloodBiomarkerCard = ({
  biomarker,
  theme,
  isHighlighted,
  isSelected,
  onClick,
}: {
  biomarker: any;
  theme: Theme;
  isHighlighted?: boolean;
  isSelected?: boolean;
  onClick?: () => void;
}) => {
  const color = getBiomarkerCategoryColor(
    biomarker.scoreCategory,
    theme,
    "biomarkers"
  );

  return (
    <div
      style={styles.bloodBiomarkerCard(theme, isSelected, isHighlighted)}
      data-highlighted={isHighlighted || undefined}
      onClick={onClick}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <div style={styles.biomarkerDot(color ?? "")} />
        {biomarker.fullName && biomarker.fullName !== biomarker.label ? (
          <CustomTooltip message={biomarker.fullName} customHeight="auto">
            <div style={styles.bloodBiomarkerName(theme)}>
              {biomarker.label}
            </div>
          </CustomTooltip>
        ) : (
          <div style={styles.bloodBiomarkerName(theme)}>{biomarker.label}</div>
        )}
      </div>
      <CustomTooltip
        message={
          BIOMARKER_CATEGORY_LABELS[biomarker.scoreCategory]
            ? `Status: ${BIOMARKER_CATEGORY_LABELS[biomarker.scoreCategory]}`
            : NO_STATUS_LABEL
        }
        customHeight="auto"
      >
        <div style={styles.scoreBadge(theme)}>
          <span style={styles.scoreBadgeText(color ?? "")}>
            {/* `displayValue` is set for qualitative / range biomarkers, whose
                value the report payload can't express numerically. */}
            {biomarker.displayValue ??
              `${biomarker.value} ${biomarker.unit ?? ""}`}
          </span>
        </div>
      </CustomTooltip>
    </div>
  );
};

export default SystemDetails;

const styles = {
  container: (theme: Theme) => ({
    flex: 1,
    display: "flex",
    flexDirection: "column" as "column",
    flexGrow: 1,
    minHeight: 0,
  }),
  inner: (theme: Theme) => ({
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    padding: "12px",
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
  }),

  // Section headers
  sectionHeader: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "4px",
  },
  sectionTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "1.4",
    letterSpacing: "-0.28px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  sectionSubtitle: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),

  systemTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "18px",
    lineHeight: "1.4",
    letterSpacing: "-0.36px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),

  // PNOE metrics section
  metricsSection: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
  },

  // Metric card
  metricCard: (
    theme: Theme,
    isSelected?: boolean,
    isHighlighted?: boolean
  ) => ({
    border: `1px solid ${
      isSelected
        ? theme.colors_ds.PRIMARY_ICON_MAIN
        : theme.colors_ds.NEUTRAL_BORDER_WEAK
    }`,
    borderRadius: "8px",
    overflow: "hidden" as const,
    backgroundColor: isHighlighted
      ? theme.colorTokens.P[10]
      : theme.colors_ds.NEUTRAL_BG_MAIN,
    cursor: "pointer",
    boxShadow: isSelected
      ? "0px 8px 20px 0px rgba(0, 0, 0, 0.06), 0px 2px 8px 0px rgba(0, 0, 0, 0.04)"
      : "none",
  }),
  metricHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px",
  },
  metricNameRow: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  metricName: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
  // Score badge (shared)
  scoreBadge: (theme: Theme) => ({
    backgroundColor: theme.colorTokens.N[10],
    padding: "4px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),
  scoreBadgeText: (color: string) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "12px",
    letterSpacing: "-0.13px",
    color: color,
    whiteSpace: "nowrap" as const,
  }),

  // Biomarkers area inside metric card
  metricBiomarkersArea: (theme: Theme, isHighlighted?: boolean) => ({
    backgroundColor: isHighlighted
      ? theme.colorTokens.P[10]
      : theme.colorTokens.N[30],
    padding: "12px",
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
  }),
  biomarkerItemCard: (theme: Theme, isSelected?: boolean) => ({
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${
      isSelected
        ? theme.colors_ds.PRIMARY_ICON_MAIN
        : theme.colors_ds.NEUTRAL_BORDER_WEAK
    }`,
    borderRadius: "8px",
    padding: "12px",
    overflow: "hidden" as const,
    cursor: "pointer",
    boxShadow: isSelected
      ? "0px 8px 20px 0px rgba(0, 0, 0, 0.06), 0px 2px 8px 0px rgba(0, 0, 0, 0.04)"
      : "none",
  }),
  biomarkerItemRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  },
  biomarkerDotAndName: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flex: 1,
  },
  biomarkerDot: (color: string) => ({
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: color,
    flexShrink: 0,
  }),
  biomarkerName: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  biomarkerValue: (color: string) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "12px",
    letterSpacing: "-0.13px",
    color: color,
    whiteSpace: "nowrap" as const,
    flexShrink: 0,
  }),

  // Blood biomarkers
  bloodKindGroup: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
  },
  bloodBiomarkerCard: (
    theme: Theme,
    isSelected?: boolean,
    isHighlighted?: boolean
  ) => ({
    backgroundColor: isHighlighted
      ? theme.colorTokens.P[10]
      : theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${
      isSelected
        ? theme.colors_ds.PRIMARY_ICON_MAIN
        : theme.colors_ds.NEUTRAL_BORDER_WEAK
    }`,
    borderRadius: "8px",
    padding: "8px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    cursor: "pointer",
    boxShadow: isSelected
      ? "0px 8px 20px 0px rgba(0, 0, 0, 0.06), 0px 2px 8px 0px rgba(0, 0, 0, 0.04)"
      : "none",
  }),
  bloodBiomarkerName: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),

  // Empty biomarker state (Health overview design)
  emptyBiomarkers: (theme: Theme) => ({
    border: `1px dashed ${theme.colors_ds.NEUTRAL_BORDER_DEFAULT}`,
    borderRadius: "8px",
    padding: "24px 16px",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "4px",
  }),
  emptyBiomarkersTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "13px",
    lineHeight: "18px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  emptyBiomarkersSubtitle: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),

  // Affiliate "Manage {system} Biomarkers" entry
};
